<!-- 
Shows the action buttons, warnings and tabs for the levy collection provider page.
-->
<template>
  <div class="mt-0">
    <v-btn 
      color="default" class="mt-0 ml-1 hidden-lg-and-up" 
      @click.stop="showActionsDrawer = !showActionsDrawer"
    >
      <v-icon class="mr-2">list</v-icon>
      Actions
    </v-btn>
    <v-btn 
      :to="`/providers/${providerId}`" 
      color="default" class="mt-0 ml-1 hidden-lg-and-up"
    >
      <v-icon class="mr-2">school</v-icon>
      Go to provider
    </v-btn>
    <our-warning-chips 
      :levy-collection-id="levyCollectionId"
      :provider-id="providerId"
      :warnings="levyCollectionProvider.warnings" 
    />
    <v-container fluid pa-0>
      <v-layout row>
        <v-flex md12 lg9>
          <v-card class="home ml-1">
            <v-tabs slider-color="primary" show-arrows>
              <v-tab key="calculation" :to="getUrl('calculation')">
                Levy calculation
                <our-warnings-tooltip :warnings="levyCollectionProvider.warnings" :filter="['IncompleteLevyParameters']"/>
              </v-tab>
              <v-tab v-if="!isSandbox" key="invoice" :to="getUrl('invoice')">
                Invoice
                <our-warnings-tooltip :warnings="levyCollectionProvider.warnings" :filter="['InvoiceRaisedButNotSent', 'InvoiceOverdue', 'SapReconciliationWarning', 'LatePaymentRequiresReview']"/>
              </v-tab>
              <v-tab v-if="!isSandbox" key="notices" :to="getUrl('notices')">
                Notices
              </v-tab>
              <v-tab v-if="!isSandbox" key="contacts" :to="getUrl('contacts')">
                Contacts
                <our-warnings-tooltip :warnings="levyCollectionProvider.warnings" :filter="['IncompleteContacts']"/>
              </v-tab>
              <v-tab v-if="!isSandbox" key="notes" :to="getUrl('notes')">
                Notes&nbsp;
                <span v-if="levyCollectionProvider.noteCount > 0" class="caption">({{ levyCollectionProvider.noteCount }})</span>
              </v-tab>
              <v-tab key="history" :to="getUrl('history')">
                History
              </v-tab>
            </v-tabs>
            <slot/>
          </v-card>
        </v-flex>
        <v-flex lg3 hidden-md-and-down>
          <v-card class="home" color="transparent" style="border: none!important;">
            <provider-actions-panel :levy-collection-id="levyCollectionId" :provider-id="providerId" />
          </v-card>
        </v-flex>
        <v-navigation-drawer
          v-show="showActionsDrawer"
          v-model="actionsDrawer"
          absolute
          temporary
          right
        >
          <provider-actions-panel :levy-collection-id="levyCollectionId" :provider-id="providerId" />
        </v-navigation-drawer>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import PaymentStatusDialog from "@/components/levy-collection-providers/PaymentStatusDialog.vue";
import ProviderActionsPanel from "@/components/levy-collection-provider-tasks/ProviderActionsPanel.vue";
import StatusTimeline from "@/components/levy-collections/StatusTimeline.vue";
import * as lcpAction from "@/store/levy-collection-providers/action-types";
import * as lcAction from "@/store/levy-collections/action-types";
import * as claim from "@/store/claim-types";
import filterMixins from "@/mixins/filter.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import overrideMixins from "@/mixins/override.mixins";

export default {
  components: {
    PaymentStatusDialog,
    ProviderActionsPanel,
    StatusTimeline
  },
  mixins: [filterMixins, lookupMixins, overrideMixins],
  props: {
    levyCollectionId: {
      required: true,
      type: Number
    },
    providerId: {
      required: true,
      type: Number
    }
  },
  data() {
    return {
      actionsDrawer: null,
      showActionsDrawer: false
    };
  },
  computed: {
    state() {
      return this.$store.state.levyCollectionProviders.view;
    },
    loading() {
      return this.$store.state.levyCollectionProviders.view.loading;
    },
    loaded() {
      return this.$store.state.levyCollectionProviders.view.loaded;
    },
    busy() {
      return this.loading || this.$store.state.levyCollectionProviders.task.processing;
    },
    levyCollectionProvider() {
      return this.$store.state.levyCollectionProviders.view.data;
    },
    levyCollection() {
      return this.$store.state.levyCollections.view.data;
    },
    isSandbox() {
      return this.doesLookupHaveAttribute(this.levyCollection.type, "IsSandbox", this.lookups.levyCollectionTypes);
    },
    canEdit() {
      return this.$store.getters.claims.includes(claim.SAP_DISMISS_RECONCILIATION_WARNING);
    },
    invoiceRaisedButNotSent() {
      return this.levyCollectionProvider.status == "InvoiceRaised";
    },
    invoiceLatePaymentRequiresReview() {
      return this.levyCollectionProvider.status == "InvoiceCleared";
    }
  },
  watch: {
    levyCollectionId() {
      this.refresh();
    },
    providerId() {
      this.refresh();
    },
    loading() {
      if (!this.loading) {
        dispatchEvent(new Event("resize"));
      }
    },
    showActionsDrawer() {
      if (this.showActionsDrawer && !this.actionsDrawer) {
        this.$nextTick(() => (this.actionsDrawer = true));
      }
    },
    actionsDrawer() {
      if (this.actionsDrawer == false && this.showActionsDrawer) {
        setTimeout(() => {
          this.showActionsDrawer = false;
          this.actionsDrawer = null;
        }, 400);
      }
    }
  },
  mounted() {
    this.refresh();
  },
  methods: {
    getUrl(tab) {
      return `/levy-collections/${this.levyCollectionId}/providers/${this.providerId}/${tab}`;
    },
    refresh() {
      this.$store.dispatch(lcpAction.VIEW_REFRESH, {
        levyCollectionId: this.levyCollectionId,
        providerId: this.providerId
      });
      this.$store.dispatch(lcAction.VIEW_REFRESH, this.levyCollectionId);
    },
    async overrideChanged() {
      if (
        await this.$store.dispatch(lcpAction.EXISTS, {
          providerId: this.providerId,
          levyCollectionId: this.levyCollectionId
        })
      ) {
        if (!this.$store.state.levyCollectionProviders.exists.exists) {
          this.$router.push(`/levy-collections/${this.levyCollectionId}/providers`);
        }
      }
    },
    async addRemoveFavourite() {
      const data = {
        levyCollectionId: this.levyCollectionId,
        providerId: this.providerId,
        type: this.levyCollectionProvider.favourite ? "Unfavourite" : "Favourite"
      };
      await this.$store.dispatch(lcpAction.TASK_RUN, data);
    }
  }
};
</script>

<style lang="scss">
.tab-warning {
  position: relative;
  top: -1px;
}
.our-warnings {
  .v-alert {
    div {
      align-self: start;
    }
  }
}
</style>